import { useAuth0 } from '@auth0/auth0-react'
import { AutoShipDropdown, DropdownOption, RadioButtonGroup } from '@dfh-corp-sites/ui'
import React, { useCallback, useMemo } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import useSubmitFormOnDirty from '../../../hooks/useSubmitFormOnDirty'
import { OrderType } from '../../../lib/cart/item'
import { autoShipIntervalOptions } from '../../../lib/cart/item-control-options'
import { CartItem } from '../../../lib/cart/types'
import { useTestKitProducts } from '../../../lib/products/hooks/useTestKitProducts'
import { logAndCaptureException } from '../../../utils/errorTools'
import UpdateCartItemErrorAlert from '../../cart/UpdateCartItemErrorAlert'
import { cn } from '../../utils/cn'

export interface CartItemOrderTypeControlProps {
  className?: string
  disabled: boolean
  item: CartItem

  updateCartItem(props: {
    autoShipInterval?: number
    orderType?: number
  }): Promise<{ error?: Error }>
}

interface FormValues {
  orderType?: string
  autoShipInterval?: DropdownOption
}

const CartItemOrderTypeControl: React.FC<CartItemOrderTypeControlProps> = ({
  disabled,
  item,
  updateCartItem,
}) => {
  const { isAuthenticated } = useAuth0()
  // const { featureFlags } = useSiteMetadata() ?? {}
  // const showAutoShipAndSaveToUserGroup = useShowAutoShipAndSaveToUserGroup()
  // const showAutoShipAndSave = !!featureFlags?.autoShipAndSave && showAutoShipAndSaveToUserGroup
  const defaultOrderType = useMemo(() => {
    const itemOrderType = item.item_attributes?.dfh_order_type
    switch (itemOrderType) {
      case OrderType.SingleOrder:
        return OrderType.SingleOrder
      case OrderType.AutoShip:
        return OrderType.AutoShip
      default:
        return OrderType.SingleOrder
    }
  }, [item.item_attributes?.dfh_order_type])

  const defaultAutoShipIntervalOption = useMemo(() => {
    const itemAutoShipInterval = item.item_attributes?.dfh_autoship_interval?.toString() || '1'
    return autoShipIntervalOptions.find(({ value }) => value === itemAutoShipInterval)
  }, [item.item_attributes?.dfh_autoship_interval])

  const { control, errors, formState, handleSubmit, reset, setError, watch } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      orderType: defaultOrderType.toString(),
      ...(defaultOrderType === OrderType.AutoShip && {
        autoShipInterval: defaultAutoShipIntervalOption,
      }),
    },
  })

  const currentOrderType = watch('orderType', defaultOrderType.toString())

  const onSubmit: SubmitHandler<FormValues> = useCallback(
    async ({ autoShipInterval, orderType }) => {
      const autoShipIntervalNumber = Number(autoShipInterval?.value)
      const orderTypeNumber = Number(orderType)

      // parse string values as numbers for comparison with cart data
      if (Number.isNaN(orderTypeNumber)) {
        const parseError = new Error(`Unable to parse orderType "${orderType}" as number`)
        logAndCaptureException(parseError)
        setError('orderType', { message: parseError.message })
        return
      }
      if (orderTypeNumber === OrderType.AutoShip && Number.isNaN(autoShipIntervalNumber)) {
        const parseError = new Error(
          `Unable to parse autoShipInterval "${autoShipInterval}" as number`,
        )
        logAndCaptureException(parseError)
        setError('autoShipInterval', { message: parseError.message })
        return
      }

      const itemOrderType = item.item_attributes?.dfh_order_type
      const itemAutoShipInterval = item.item_attributes?.dfh_autoship_interval

      // avoid update orderType and autoShipInterval match cart item attributes
      if (
        orderTypeNumber === itemOrderType &&
        (orderTypeNumber === OrderType.SingleOrder ||
          (orderTypeNumber === OrderType.AutoShip &&
            autoShipIntervalNumber === itemAutoShipInterval))
      ) {
        return
      }

      const result = await updateCartItem({
        orderType: orderTypeNumber,
        ...(orderTypeNumber === OrderType.AutoShip && {
          autoShipInterval: autoShipIntervalNumber,
        }),
      })

      if (result.error) {
        setError('orderType', { message: result.error.message, type: 'server' })
      }
    },
    [
      item.item_attributes?.dfh_autoship_interval,
      item.item_attributes?.dfh_order_type,
      setError,
      updateCartItem,
    ],
  )

  const { resetForm } = useSubmitFormOnDirty({
    defaultValues: useMemo(
      () => ({
        orderType: defaultOrderType.toString(),
        ...(defaultOrderType === OrderType.AutoShip && {
          autoShipInterval: defaultAutoShipIntervalOption,
        }),
      }),
      [defaultAutoShipIntervalOption, defaultOrderType],
    ),
    formState,
    handleSubmit,
    onSubmit,
    reset,
  })

  const { isTestKitSku } = useTestKitProducts()
  const showAutoShip = !isTestKitSku(item.product.sku || '')

  if (!showAutoShip) {
    return null
  }
  if (!isAuthenticated) {
    return null
  }

  return (
    <div className={cn('mt-3 flex w-full flex-col gap-3')}>
      {errors.orderType && (
        <UpdateCartItemErrorAlert onCancel={resetForm} onRetry={handleSubmit(onSubmit)} />
      )}
      <RadioButtonGroup
        className="flex-col md:flex-row gap-3.5 md:gap-5 items-start md:items-center"
        defaultValue={defaultOrderType.toString()}
        name="orderType"
        control={control}
        radioButtonOptions={[
          {
            id: `${item.id}_single_order`,
            label: (
              <span className="text-xs max-w-14 flex flex-wrap leading-none">One time order</span>
            ),
            value: OrderType.SingleOrder.toString(),
          },
          {
            id: `${item.id}_auto_ship`,
            label: (
              <span className="text-xs max-w-20 flex flex-wrap leading-none">
                Subscribe to save 5%
              </span>
              // <AutoShipControlLabel
              //   className="auto-ship-label-cart"
              //   showSaveText={showAutoShipAndSave}
              //   disabled={disabled}
              // />
            ),
            value: OrderType.AutoShip.toString(),
          },
        ]}
        disabled={disabled}
      />
      {currentOrderType === OrderType.AutoShip.toString() && (
        <AutoShipDropdown
          control={control}
          defaultValue={defaultAutoShipIntervalOption}
          name="autoShipInterval"
          options={autoShipIntervalOptions}
          prompt="Ship every:"
          disabled={disabled}
        />
      )}
      {/* {currentOrderType !== OrderType.AutoShip.toString() ? (
        <p className="small-paragraph small-margin-bottom">
          {showAutoShipAndSave
            ? 'Set up Auto-Ship & Save 5% on this product. Your Auto-Ship can be canceled at any time.'
            : 'Set up Auto-Ship and never run out of the supplements you love! Cancel at any time.'}
        </p>
      ) : (
        <>
          {showAutoShipAndSave && (
            <p className="small-paragraph small-margin-bottom">
              You will receive a 5% discount on this product.
            </p>
          )}
          <AutoShipDropdown
            control={control}
            defaultValue={defaultAutoShipIntervalOption}
            name="autoShipInterval"
            options={autoShipIntervalOptions}
            prompt="Ship every:"
            disabled={disabled}
          />
        </>
      )} */}
    </div>
  )
}

export default CartItemOrderTypeControl
